'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CompanyContext } from '../company-context';

const ENDPOINT = '/api/connect/company-context';

interface CompanyContextResponse {
    context: CompanyContext | null;
}

async function readJson<T>(res: Response): Promise<T> {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
    return body as T;
}

/** The org's saved company details. `saved` is null while loading, when the
 * org has saved none, and when the read fails — the popover then starts from
 * the org's name, so a missing table or an outage never blocks a question. */
export function useCompanyContext(enabled: boolean) {
    const queryClient = useQueryClient();
    const queryKey = ['connect-company-context'];

    const query = useQuery({
        queryKey,
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
        queryFn: async () =>
            (await readJson<CompanyContextResponse>(await fetch(ENDPOINT))).context,
    });

    const save = useMutation({
        mutationFn: async (context: CompanyContext) =>
            (
                await readJson<CompanyContextResponse>(
                    await fetch(ENDPOINT, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(context),
                    }),
                )
            ).context,
        onSuccess: (context) => queryClient.setQueryData(queryKey, context),
    });

    return {
        saved: query.data ?? null,
        isLoading: query.isLoading,
        save: save.mutateAsync,
    };
}
